import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { useCallback, useRef, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, DownloadIcon } from "@icons";
import { StandardModal } from "@design-system/Modal/Modal";
import classNames from "classnames";
import { Separator } from "@design-system/Separator/Separator";
import { FinancialDocumentPreview } from "../FinancialDocumentDetails/FinancialDocumentPreview";
import { useDownloadFinancialDocumentFile } from "../FinancialDocumentDetails/useDownloadFinancialDocumentFile";
import { DocumentName } from "../DocumentName";
import { useFinancialDocumentFilters } from "../useFinancialDocumentFilters";
import { FinancialDocumentSelectContent } from "./FinancialDocumentSelectContent";
import { DocumentSelectVariant } from "./financialDocumentSelectViewConfig";
import styles from "./FinancialDocumentSelect.module.scss";

interface Props {
    variant: DocumentSelectVariant;
    cta: (document: FinancialDocumentListItem) => React.ReactNode;
    sidebar?: React.ReactNode;
    attachedDocumentIds?: number[];
    attachDocuments?: (documentIds: number[]) => void;
    getRowSelectionDisabledTooltip?: (
        document: FinancialDocumentListItem,
    ) => string | undefined;
    onDocumentsChanged?: (documents: FinancialDocumentListItem[]) => void;
    initialFilters?: Parameters<typeof useFinancialDocumentFilters>[1];
}

export const FinancialDocumentSelect: React.FC<Props> = ({
    variant,
    cta,
    sidebar,
    attachedDocumentIds,
    attachDocuments,
    getRowSelectionDisabledTooltip,
    onDocumentsChanged,
    initialFilters,
}) => {
    const [previewedDocument, setPreviewedDocument] =
        useState<FinancialDocumentListItem>();
    const [documents, setDocuments] = useState<FinancialDocumentListItem[]>([]);

    const handleDocumentsChange = useCallback(
        (changedDocuments: FinancialDocumentListItem[]) => {
            setDocuments(changedDocuments);
            onDocumentsChanged?.(changedDocuments);
        },
        [onDocumentsChanged],
    );
    const [page, setPage] = useState<number>(1);
    const onFiltersChange = useCallback(() => setPage(1), []);
    const { filters, resetFilters, handleFiltersChange } =
        useFinancialDocumentFilters(onFiltersChange, initialFilters);

    const closePreview = useCallback(() => setPreviewedDocument(undefined), []);

    const findPreviewedIndex = useCallback(
        (doc: FinancialDocumentListItem) =>
            documents.findIndex((d) => d.id === doc.id),
        [documents],
    );

    const previewNext = useCallback(() => {
        if (!previewedDocument) {
            return;
        }
        const index = findPreviewedIndex(previewedDocument);
        if (index === -1 || index + 1 >= documents.length) {
            return;
        }
        setPreviewedDocument(documents[index + 1]);
    }, [documents, previewedDocument, findPreviewedIndex]);

    const previewPrevious = useCallback(() => {
        if (!previewedDocument) {
            return;
        }
        const index = findPreviewedIndex(previewedDocument);
        if (index === -1 || index - 1 < 0) {
            return;
        }
        setPreviewedDocument(documents[index - 1]);
    }, [documents, previewedDocument, findPreviewedIndex]);

    const displayedDocument = useRef<FinancialDocumentListItem | undefined>(
        undefined,
    );
    if (previewedDocument) {
        displayedDocument.current = previewedDocument;
    }

    const { download, isDownloading } = useDownloadFinancialDocumentFile(
        displayedDocument.current ?? null,
    );

    return (
        <>
            <div
                className={classNames(styles.layout, {
                    [styles.withSidebar]: sidebar,
                })}
            >
                <header className={styles.headerArea}>
                    <div className={styles.header}>Documents</div>
                </header>
                <main className={styles.contentArea}>
                    <FinancialDocumentSelectContent
                        variant={variant}
                        cta={cta}
                        onPreviewed={setPreviewedDocument}
                        onDocumentsChange={handleDocumentsChange}
                        filters={filters}
                        page={page}
                        handleFiltersChange={handleFiltersChange}
                        resetFilters={resetFilters}
                        attachedDocumentIds={attachedDocumentIds}
                        attachDocuments={attachDocuments}
                        getRowSelectionDisabledTooltip={
                            getRowSelectionDisabledTooltip
                        }
                    />
                </main>
                {sidebar && (
                    <aside className={styles.sidebarArea}>{sidebar}</aside>
                )}
            </div>

            <StandardModal
                className="fullscreen-modal"
                show={!!previewedDocument}
                onHide={closePreview}
                onExited={() => {
                    displayedDocument.current = undefined;
                }}
                hideCloseButton
            >
                {displayedDocument.current && (
                    <div className={styles.layout}>
                        <header className={styles.headerArea}>
                            <div className={styles.previewHeader}>
                                <div className={styles.previewHeaderLeft}>
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        onClick={previewPrevious}
                                        aria-label="Previous document"
                                    >
                                        <ArrowUpIcon />
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        onClick={previewNext}
                                        aria-label="Next document"
                                    >
                                        <ArrowDownIcon />
                                    </Button>
                                    <Separator
                                        orientation="vertical"
                                        className={styles.headerSeparator}
                                    />
                                    <DocumentName
                                        fileName={
                                            displayedDocument.current.fileName
                                        }
                                        icon
                                        documentId={
                                            displayedDocument.current.id
                                        }
                                    />
                                </div>
                                <div className={styles.previewHeaderRight}>
                                    {cta(displayedDocument.current)}
                                    <ButtonWithLoader
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        aria-label="Download document"
                                        onClick={() => {
                                            void download();
                                        }}
                                        loading={isDownloading}
                                        tooltip="Download"
                                    >
                                        <DownloadIcon />
                                    </ButtonWithLoader>
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        onClick={closePreview}
                                        aria-label="Close"
                                    >
                                        <CloseIcon />
                                    </Button>
                                </div>
                            </div>
                        </header>
                        <main className={styles.contentArea}>
                            <FinancialDocumentPreview
                                financialDocument={displayedDocument.current}
                            />
                        </main>
                    </div>
                )}
            </StandardModal>
        </>
    );
};
